import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import ImageManager from "../components/admin/ImageManager";
import { CalendarDays, ImageIcon } from "lucide-react";

const STATUS_COLOR = {
  confirmed: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  cancelled: "bg-red-100 text-red-800",
  blocked: "bg-slate-200 text-slate-800",
};

export default function AdminDashboardPage() {
  const { admin, loading, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("bookings");

  const load = async () => {
    setBusy(true);
    try {
      const [b, s, f] = await Promise.all([api.get("/admin/bookings"), api.get("/admin/stats"), api.get("/admin/files")]);
      setBookings(b.data);
      setStats(s.data);
      setFiles(f.data);
    } catch (e) { toast.error("Load failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (admin) load(); }, [admin]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">···</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Updated");
      await load();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))] pt-28 pb-16">
      <div className="container-x">
        <div className="flex items-center justify-between mb-12">
          <div>
            <div className="overline mb-2">{t.admin.title}</div>
            <h1 className="font-serif font-light text-4xl text-[hsl(var(--foreground))]">{t.admin.dashboard}</h1>
          </div>
          <button data-testid="admin-logout" onClick={() => { logout(); nav("/admin/login"); }} className="btn-ghost">{t.admin.logout}</button>
        </div>

        {/* Tab navigation */}
        <div className="flex gap-1 mb-8 border-b border-[hsl(var(--foreground))]/10">
          <button
            data-testid="tab-bookings"
            onClick={() => setTab("bookings")}
            className={`flex items-center gap-2 px-5 py-3 text-xs tracking-wider uppercase font-medium border-b-2 transition-colors ${
              tab === "bookings"
                ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]"
                : "border-transparent text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))]"
            }`}
          >
            <CalendarDays size={16} />
            Buchungen
          </button>
          <button
            data-testid="tab-images"
            onClick={() => setTab("images")}
            className={`flex items-center gap-2 px-5 py-3 text-xs tracking-wider uppercase font-medium border-b-2 transition-colors ${
              tab === "images"
                ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]"
                : "border-transparent text-[hsl(var(--foreground))]/60 hover:text-[hsl(var(--foreground))]"
            }`}
          >
            <ImageIcon size={16} />
            Bilder ({files.length})
          </button>
        </div>

        {tab === "bookings" && (
          <>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[hsl(var(--foreground))]/10 border border-[hsl(var(--foreground))]/10 mb-8">
            <Stat label="Total" value={stats.total} />
            <Stat label="Confirmed" value={stats.confirmed} />
            <Stat label="Pending" value={stats.pending} />
            <Stat label={t.admin.revenue} value={`€${Number(stats.revenue_eur).toFixed(0)}`} />
          </div>
        )}

        {/* Manual block form — for phone/WhatsApp bookings */}
        <BlockForm onDone={load} />

        <div className="overline mb-4 mt-12">{t.admin.bookings}</div>
        {bookings.length === 0 ? (
          <p className="text-[hsl(var(--foreground))]/80">{t.admin.noBookings}</p>
        ) : (
          <div className="overflow-x-auto border border-[hsl(var(--foreground))]/10">
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--moss))]">
                <tr className="text-left">
                  {["Unit", "Guest", "Email", "Check-in", "Check-out", "Nights", "Guests", "Total", "Status", "ICS", "Action"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-semibold text-[hsl(var(--foreground))]/85">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[hsl(var(--foreground))]/10 text-[hsl(var(--foreground))]/85">
                    <td className="px-4 py-3 font-serif">{b.unit_name || b.unit_id || "—"}</td>
                    <td className="px-4 py-3">{b.first_name} {b.last_name}</td>
                    <td className="px-4 py-3">{b.email}</td>
                    <td className="px-4 py-3">{b.check_in_iso}</td>
                    <td className="px-4 py-3">{b.check_out_iso}</td>
                    <td className="px-4 py-3">{b.nights}</td>
                    <td className="px-4 py-3">{b.guests}</td>
                    <td className="px-4 py-3 font-medium text-[hsl(var(--accent))]">€{Number(b.total_eur).toFixed(0)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-[11px] uppercase tracking-wider ${STATUS_COLOR[b.status] || "bg-slate-100"}`}>{b.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <a
                        href={`${api.defaults.baseURL}/bookings/${b.id}/ics`}
                        download
                        data-testid={`ics-${b.id}`}
                        className="text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--accent))] hover:text-[hsl(var(--foreground))] border-b border-[hsl(var(--accent))]/40 pb-0.5"
                      >
                        .ics ↓
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <select data-testid={`status-select-${b.id}`} value={b.status} onChange={(e) => updateStatus(b.id, e.target.value)} className="border border-[hsl(var(--foreground))]/20 px-2 py-1 text-xs bg-[hsl(var(--card))] text-[hsl(var(--foreground))]">
                        <option value="pending">pending</option>
                        <option value="confirmed">confirmed</option>
                        <option value="cancelled">cancelled</option>
                        <option value="blocked">blocked</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {busy && <div className="p-4 text-sm text-[hsl(var(--foreground))]/80">Loading...</div>}
          </div>
        )}
          </>
        )}

        {tab === "images" && (
          <ImageManager files={files} onRefresh={load} />
        )}
      </div>
    </main>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[hsl(var(--card))] p-6">
      <div className="overline">{label}</div>
      <div className="mt-2 font-serif text-3xl text-[hsl(var(--accent))]">{value}</div>
    </div>
  );
}

function BlockForm({ onDone }) {
  const [unitId, setUnitId] = useState("zirben");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!checkIn || !checkOut) { toast.error("Bitte An- und Abreise angeben"); return; }
    setBusy(true);
    try {
      await api.post("/admin/block", {
        unit_id: unitId,
        check_in: checkIn,
        check_out: checkOut,
        note: note || "Manuelle Sperre",
      });
      toast.success("Zeitraum gesperrt");
      setCheckIn(""); setCheckOut(""); setNote("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sperre fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <form
      data-testid="block-form"
      onSubmit={submit}
      className="bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/10 p-6 md:p-7"
    >
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="overline">Zeitraum sperren</div>
          <h2 className="font-serif text-2xl text-[hsl(var(--foreground))] mt-1">Manuelle Buchung eintragen</h2>
        </div>
        <div className="text-[11px] text-[hsl(var(--foreground))]/72 hidden md:block">Für Telefon-/WhatsApp-Buchungen</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="md:col-span-1">
          <label className="overline block mb-2">Haus</label>
          <select
            data-testid="block-unit"
            value={unitId}
            onChange={(e) => setUnitId(e.target.value)}
            className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--foreground))]/15 text-[hsl(var(--foreground))] py-2.5 px-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
          >
            <option value="zirben">Zirben Chalet</option>
            <option value="landhaus">Landhaus</option>
            <option value="combo">Beide (Combo)</option>
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Anreise</label>
          <input
            data-testid="block-checkin"
            type="date"
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
            required
            className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--foreground))]/15 text-[hsl(var(--foreground))] py-2.5 px-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
          />
        </div>
        <div>
          <label className="overline block mb-2">Abreise</label>
          <input
            data-testid="block-checkout"
            type="date"
            value={checkOut}
            onChange={(e) => setCheckOut(e.target.value)}
            required
            className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--foreground))]/15 text-[hsl(var(--foreground))] py-2.5 px-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
          />
        </div>
        <div className="md:col-span-1">
          <label className="overline block mb-2">Notiz (optional)</label>
          <input
            data-testid="block-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="z. B. Familie Müller · Telefon"
            className="w-full bg-[hsl(var(--input))] border border-[hsl(var(--foreground))]/15 text-[hsl(var(--foreground))] py-2.5 px-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
          />
        </div>
        <div className="flex items-end">
          <button
            data-testid="block-submit"
            type="submit"
            disabled={busy}
            className="btn-primary w-full !py-3 justify-center"
          >
            {busy ? "..." : "Sperren"}
          </button>
        </div>
      </div>
    </form>
  );
}
